import { useState, useEffect } from 'react';
import { ArrowLeft, Copy, Check, RefreshCw, Plus, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireBusiness } from '@/components/RequireBusiness';
import { useApp } from '@/contexts/AppContext';
import { AddCategoryModal } from '@/components/AddCategoryModal';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const tones = ['Professional', 'Friendly', 'Casual', 'Enthusiastic', 'Sincere', 'Grateful'];
const styles = ['Detailed', 'Concise', 'Story-based', 'Feature-focused', 'Emotional', 'Factual'];
const wordLimits = ['Short (20-40 words)', 'Medium (40-70 words)', 'Long (70-100 words)'];
const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian', 'Dutch', 'Russian', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi'];

interface GeneratedReviewDisplay {
  id: string;
  text: string;
  category: string;
  keywords: string[];
}

export default function GenerateReviewPage() {
  return (
    <RequireBusiness>
      <GenerateReviewContent />
    </RequireBusiness>
  );
}

function GenerateReviewContent() {
  const navigate = useNavigate();
  const { business, categories, saveReviews } = useApp();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [generatedReviews, setGeneratedReviews] = useState<GeneratedReviewDisplay[]>([]);
  const [usedReviewTexts, setUsedReviewTexts] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    keywords: '',
    usp: '',
    language: 'English',
    tone: 'Professional',
    style: 'Detailed',
    wordLimit: 'Medium (40-70 words)',
    numberOfReviews: 3,
    additionalInstructions: '',
  });

  // Set initial category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);

  // Auto-populate form fields from selected category
  useEffect(() => {
    if (activeCategory_obj) {
      setFormData(prev => ({
        ...prev,
        keywords: activeCategory_obj.keywords?.join(', ') || prev.keywords,
        usp: activeCategory_obj.usp || prev.usp,
      }));
    }
  }, [activeCategory]);
  const handleChange = (field: string, value: string | number) => {
    setFormData({ ...formData, [field]: value });
  };

  const activeCategory_obj = categories.find(c => c.id === activeCategory);
  const effectiveLocation = activeCategory_obj?.location || business?.location;

  // Generate a single replacement review
  const generateSingleReview = async (): Promise<GeneratedReviewDisplay | null> => {
    if (!business) return null;

    try {
      const { data, error } = await supabase.functions.invoke('generate-reviews', {
        body: {
          businessName: business.name,
          industry: business.industry,
          category: activeCategory_obj?.name || 'General',
          location: effectiveLocation,
          serviceArea: business.service_area,
          keywords: formData.keywords,
          usp: formData.usp,
          language: formData.language,
          tone: formData.tone,
          style: formData.style,
          wordLimit: formData.wordLimit,
          numberOfReviews: 1,
          additionalInstructions: formData.additionalInstructions,
          excludeTexts: Array.from(usedReviewTexts),
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const reviews = data.reviews as { text: string; keywords: string[] }[];
      if (reviews.length === 0) return null;

      const newReview = reviews[0];
      
      // Save the new review to the database
      await saveReviews([{
        business_id: business.id,
        category_id: activeCategory || null,
        review_text: newReview.text,
        category_name: activeCategory_obj?.name || 'General',
        keywords: newReview.keywords || formData.keywords.split(',').map(k => k.trim()),
        usp: formData.usp || null,
        tone: formData.tone,
        style: formData.style,
        word_limit: formData.wordLimit,
        language: formData.language,
      }]);

      return {
        id: `temp-${Date.now()}-new`,
        text: newReview.text,
        category: activeCategory_obj?.name || 'General',
        keywords: newReview.keywords || formData.keywords.split(',').map(k => k.trim()),
      };
    } catch (err) {
      console.error('Error generating replacement review:', err);
      return null;
    }
  };

  const handleGenerate = async () => {
    if (!business) {
      toast.error('Please complete business setup first');
      return;
    }

    setIsGenerating(true);
    // Clear used texts when generating fresh batch
    setUsedReviewTexts(new Set());

    try {
      const { data, error } = await supabase.functions.invoke('generate-reviews', {
        body: {
          businessName: business.name,
          industry: business.industry,
          category: activeCategory_obj?.name || 'General',
          location: effectiveLocation,
          serviceArea: business.service_area,
          keywords: formData.keywords,
          usp: formData.usp,
          language: formData.language,
          tone: formData.tone,
          style: formData.style,
          wordLimit: formData.wordLimit,
          numberOfReviews: formData.numberOfReviews,
          additionalInstructions: formData.additionalInstructions,
        },
      });

      if (error) throw error;

      if (data.error) {
        toast.error(data.error);
        return;
      }

      const reviews = data.reviews as { text: string; keywords: string[] }[];
      
      const displayReviews: GeneratedReviewDisplay[] = reviews.map((r, i) => ({
        id: `temp-${Date.now()}-${i}`,
        text: r.text,
        category: activeCategory_obj?.name || 'General',
        keywords: r.keywords || formData.keywords.split(',').map(k => k.trim()),
      }));

      setGeneratedReviews(displayReviews);

      const reviewsToSave = reviews.map((r) => ({
        business_id: business.id,
        category_id: activeCategory || null,
        review_text: r.text,
        category_name: activeCategory_obj?.name || 'General',
        keywords: r.keywords || formData.keywords.split(',').map(k => k.trim()),
        usp: formData.usp || null,
        tone: formData.tone,
        style: formData.style,
        word_limit: formData.wordLimit,
        language: formData.language,
      }));

      await saveReviews(reviewsToSave);
      toast.success(`Generated ${reviews.length} reviews!`);
    } catch (err) {
      console.error('Error generating reviews:', err);
      toast.error('Failed to generate reviews. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyAndReplace = async (review: GeneratedReviewDisplay) => {
    // Copy to clipboard
    navigator.clipboard.writeText(review.text);
    setCopiedId(review.id);
    toast.success('Review copied!');

    // Track this review as used
    setUsedReviewTexts(prev => new Set(prev).add(review.text));

    // Remove the copied review from the list
    setGeneratedReviews(prev => prev.filter(r => r.id !== review.id));

    // Generate a new review to replace it
    setIsRegenerating(true);
    const newReview = await generateSingleReview();
    
    if (newReview) {
      // Add new review at the end
      setGeneratedReviews(prev => [...prev, newReview]);
      // Track new review text
      setUsedReviewTexts(prevUsed => new Set(prevUsed).add(newReview.text));
    }
    
    setIsRegenerating(false);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyAndPost = (review: GeneratedReviewDisplay) => {
    navigator.clipboard.writeText(review.text);
    // Track this review as used
    setUsedReviewTexts(prev => new Set(prev).add(review.text));
    navigate('/post-review', { state: { review } });
  };

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-muted rounded-xl transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="section-title">Generate Review</h1>
        </div>

        {/* Category Tabs */}
        <div className="mb-6 overflow-x-auto hide-scrollbar">
          <div className="flex gap-2 pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`tab-button ${
                  activeCategory === category.id
                    ? 'tab-button-active'
                    : 'tab-button-inactive'
                }`}
              >
                {category.name}
              </button>
            ))}
            <button
              onClick={() => setShowAddCategory(true)}
              className="tab-button border border-dashed border-border text-muted-foreground hover:bg-muted flex items-center gap-1.5"
            >
              <Plus size={16} />
              Add
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <div className="soft-card animate-fade-in">
            <h2 className="font-semibold mb-5">Review Parameters</h2>
            
            <div className="space-y-5">
              {/* Business Name */}
              <div>
                <Label className="modern-label">Business Name</Label>
                <Input
                  value={business?.name || 'Your Business'}
                  disabled
                  className="modern-input bg-muted"
                />
              </div>

              {/* Review Category */}
              <div>
                <Label className="modern-label">Review Category</Label>
                <Input
                  value={activeCategory_obj?.name || 'Select a category'}
                  disabled
                  className="modern-input bg-muted"
                />
              </div>

              {/* Keywords */}
              <div>
                <Label className="modern-label">Keywords (comma-separated)</Label>
                <Input
                  value={formData.keywords}
                  onChange={(e) => handleChange('keywords', e.target.value)}
                  placeholder="quality care, expert team, modern facility"
                  className="modern-input"
                />
              </div>

              {/* USP */}
              <div>
                <Label className="modern-label">Unique Selling Points</Label>
                <Input
                  value={formData.usp}
                  onChange={(e) => handleChange('usp', e.target.value)}
                  placeholder="24/7 support, same-day service"
                  className="modern-input"
                />
              </div>

              {/* Language & Tone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="modern-label">Language</Label>
                  <select
                    value={formData.language}
                    onChange={(e) => handleChange('language', e.target.value)}
                    className="modern-input"
                  >
                    {languages.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="modern-label">Tone</Label>
                  <select
                    value={formData.tone}
                    onChange={(e) => handleChange('tone', e.target.value)}
                    className="modern-input"
                  >
                    {tones.map((tone) => (
                      <option key={tone} value={tone}>{tone}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Style & Word Limit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="modern-label">Style</Label>
                  <select
                    value={formData.style}
                    onChange={(e) => handleChange('style', e.target.value)}
                    className="modern-input"
                  >
                    {styles.map((style) => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="modern-label">Word Limit</Label>
                  <select
                    value={formData.wordLimit}
                    onChange={(e) => handleChange('wordLimit', e.target.value)}
                    className="modern-input"
                  >
                    {wordLimits.map((limit) => (
                      <option key={limit} value={limit}>{limit} words</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Number of Reviews */}
              <div>
                <Label className="modern-label">Number of Reviews</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 5].map((num) => (
                    <button
                      key={num}
                      onClick={() => handleChange('numberOfReviews', num)}
                      className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        formData.numberOfReviews === num
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted text-muted-foreground hover:bg-accent'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Instructions */}
              <div>
                <Label className="modern-label">Additional Instructions</Label>
                <Textarea
                  value={formData.additionalInstructions}
                  onChange={(e) => handleChange('additionalInstructions', e.target.value)}
                  placeholder="Any specific details to include..."
                  className="modern-input min-h-[100px] resize-none"
                />
              </div>

              {/* Generate Button */}
              <Button 
                onClick={handleGenerate}
                disabled={isGenerating || categories.length === 0}
                className="gradient-btn w-full h-14 text-base"
                size="lg"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="mr-2 animate-spin" size={20} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={20} />
                    Generate Reviews
                  </>
                )}
              </Button>

              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground text-center">
                  Add a category first to generate reviews
                </p>
              )}
            </div>
          </div>

          {/* Output Panel */}
          <div className="soft-card animate-fade-in" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold">Generated Reviews</h2>
              {isRegenerating && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="animate-spin" size={14} />
                  <span>Adding new...</span>
                </div>
              )}
            </div>
            
            {generatedReviews.length === 0 ? (
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-border rounded-xl">
                <p className="text-muted-foreground text-center text-sm">
                  Generated reviews will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {generatedReviews.map((review, index) => (
                  <div 
                    key={review.id} 
                    className="review-card animate-fade-in"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-lg inline-block">
                      {review.category}
                    </span>
                    
                    <p className="text-sm leading-relaxed">{review.text}</p>
                    
                    <Button
                      onClick={() => handleCopyAndReplace(review)}
                      variant="outline"
                      className="w-full h-11 rounded-xl"
                      disabled={isRegenerating}
                    >
                      {copiedId === review.id ? (
                        <>
                          <Check className="mr-2 text-success" size={16} />
                          Copied! Generating new...
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2" size={16} />
                          Copy & Get New
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddCategoryModal 
        open={showAddCategory} 
        onOpenChange={setShowAddCategory} 
      />
    </AppLayout>
  );
}
